import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, Row, Col, Card, Button, Badge, Table, Form, 
  InputGroup, Modal, Alert, Dropdown, Spinner, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import { 
  Users, Building, Shield, Plus, Search, Filter, 
  Download, RefreshCw, Eye, Edit, Mail, Phone,
  UserCheck, Key, MoreVertical, UserX, UserPlus,
  Power, PowerOff, AlertCircle, CheckCircle,
  Package,
  Edit2
} from 'lucide-react';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';
import CreateUser from './CreateUser';

const UsersList = () => {
  const { user: currentUser } = useAuth();
  const { currentBoutique, boutiques } = useBoutique();

  // États principaux
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState({}); // Pour les actions individuelles
  
  // Statistiques
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    managers: 0,
    employees: 0,
    superAdmins: 0
  });
  
  // Filtres avancés
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    boutiqueId: currentUser?.role === 'SUPER_ADMIN' ? 'all' : (currentBoutique?.id || ''),
    search: '',
    dateRange: 'all', // Nouveau filtre par date
    sortBy: 'lastName', // Nouveau tri
    sortDirection: 'ASC'
  });
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // États de gestion
  const [errors, setErrors] = useState({});

  // ✅ Chargement initial et mise à jour des filtres
  useEffect(() => {
    loadUsers();
  }, [currentUser, currentBoutique, filters.boutiqueId, filters.sortBy, filters.sortDirection]);

  // ✅ Filtrage et recherche en temps réel
  useEffect(() => {
    if (filters.role !== 'all' || filters.status !== 'all' || searchQuery) {
      // Petite temporisation pour éviter trop d'appels
      const timeoutId = setTimeout(() => {
        loadUsers();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [filters.role, filters.status, searchQuery]);

  // ✅ Fonction de chargement avec gestion des rôles
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('👥 Chargement des utilisateurs avec filtres:', filters);
      
      // Préparer les options selon le rôle
      const options = {
        ...filters,
        search: searchQuery,
        page: 0,
        size: 100
      };

      let response;
      
      if (currentUser?.role === 'SUPER_ADMIN') {
        // Super Admin : peut voir tous les utilisateurs
        console.log('👑 Super Admin - chargement de tous les utilisateurs');
        response = await userService.getAllUsers(options);
      } else {
        // Manager/Employee : seulement leur boutique
        if (currentBoutique?.id) {
          console.log('👤 Manager/Employee - chargement boutique:', currentBoutique.name);
          response = await userService.getUsersByBoutique(currentBoutique.id, options);
        } else {
          throw new Error('Boutique non définie');
        }
      }
      
      console.log('📥 Réponse chargement utilisateurs:', response);

      if (response.success) {
        // Gérer différents formats de réponse
        let usersData = [];
        
        if (response.data?.content) {
          usersData = response.data.content;
        } else if (Array.isArray(response.data)) {
          usersData = response.data;
        } else if (response.data?.users) {
          usersData = response.data.users;
        } else {
          usersData = [];
        }
        
        // ✅ NOUVEAU: Tri côté client si nécessaire
        usersData = sortUsers(usersData, filters.sortBy, filters.sortDirection);
        
        setUsers(usersData);
        setUserStats(calculateStats(usersData));
        
        console.log('✅ Utilisateurs chargés:', usersData.length);
      } else {
        throw new Error(response.message || 'Erreur lors du chargement');
      }

    } catch (error) {
      console.error('❌ Erreur loadUsers:', error);
      setError(error.message || 'Erreur lors du chargement des utilisateurs');
      setUsers([]);
      setUserStats({ totalUsers: 0, activeUsers: 0, inactiveUsers: 0, managers: 0, employees: 0, superAdmins: 0 });
    } finally {
      setLoading(false);
    }
  }, [currentUser, currentBoutique, filters, searchQuery]);

  

  // ✅ NOUVEAU: Fonction de tri
  const sortUsers = (usersData, sortBy, sortDirection) => {
    return [...usersData].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      // Gestion des valeurs null/undefined
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';
      
      // Gestion des dates
      if (sortBy === 'createdAt' || sortBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Tri
      if (aValue < bValue) return sortDirection === 'ASC' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'ASC' ? 1 : -1;
      return 0;
    });
  };

  // ✅ NOUVEAU: Calculer les statistiques avec plus de détails
  const calculateStats = (usersData) => {
    const stats = {
      totalUsers: usersData.length,
      activeUsers: usersData.filter(u => isUserActive(u)).length,
      inactiveUsers: usersData.filter(u => !isUserActive(u)).length,
      managers: usersData.filter(u => u.role === 'MANAGER').length,
      employees: usersData.filter(u => u.role === 'EMPLOYEE').length,
      superAdmins: usersData.filter(u => u.role === 'SUPER_ADMIN').length
    };
    return stats;
  };

  // ✅ NOUVEAU: Fonction unifiée pour déterminer si un utilisateur est actif
  const isUserActive = (user) => {
    return user.status === 'ACTIVE' || user.isActive === true || user.is_active === true;
  };

  // ✅ Filtrage côté client avec recherche avancée
  const filteredUsers = users.filter(user => {
    // Filtrage par recherche
    if (searchQuery) {
      const searchTerm = searchQuery.toLowerCase();
      const matchesSearch = (
        (user.firstName || user.prenom || '').toLowerCase().includes(searchTerm) ||
        (user.nom || user.lastName || '').toLowerCase().includes(searchTerm) ||
        user.email?.toLowerCase().includes(searchTerm) ||
        user.username?.toLowerCase().includes(searchTerm) ||
        user.boutiqueName?.toLowerCase().includes(searchTerm) ||
        user.phone?.toLowerCase().includes(searchTerm)
      );
      if (!matchesSearch) return false;
    }

    // Filtrage par rôle
    if (filters.role !== 'all' && user.role !== filters.role) {
      return false;
    }

    // Filtrage par statut
    if (filters.status !== 'all') {
      const userStatus = isUserActive(user);
      const wantedStatus = filters.status === 'ACTIVE';
      if (userStatus !== wantedStatus) return false;
    }

    // ✅ NOUVEAU: Filtrage par date de création
    if (filters.dateRange !== 'all') {
      const userDate = new Date(user.createdAt || user.created_at);
      const now = new Date();
      const daysDiff = Math.floor((now - userDate) / (1000 * 60 * 60 * 24));
      
      switch (filters.dateRange) {
        case 'today': if (daysDiff > 0) return false; break;
        case 'week': if (daysDiff > 7) return false; break;
        case 'month': if (daysDiff > 30) return false; break;
        case 'quarter': if (daysDiff > 90) return false; break;
      }
    }

    return true;
  });

  // ✅ Gestion des changements de filtres
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ✅ Gestion des actions utilisateur
  const handleCreateUser = () => {
    setSelectedUser(null);
    setErrors({});
    setShowCreateModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setErrors({});
    setShowCreateModal(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  // ✅ NOUVEAU: Gestion unifiée de confirmation d'action
  const handleUserAction = (action, user, message) => {
    setSelectedUser(user);
    setConfirmAction({ action, user, message });
    setShowConfirmModal(true);
  };

  // ✅ NOUVEAU: Désactiver un utilisateur avec confirmation
  const handleDeactivateUser = (user) => {
    if (user.role === 'SUPER_ADMIN') {
      setError('Impossible de désactiver un Super Administrateur');
      return;
    }
    
    const message = `
      ⚠️ Désactiver l'utilisateur "${user.firstName || user.prenom} ${user.lastName || user.nom}" ?
      
      Cette action aura les conséquences suivantes :
      • L'utilisateur ne pourra plus se connecter
      • Ses sessions actives seront terminées
      • Ses accès seront suspendus
      
      Cette action peut être annulée plus tard.
    `;
    
    handleUserAction('deactivate', user, message);
  };

  // ✅ NOUVEAU: Activer un utilisateur avec confirmation
  const handleActivateUser = (user) => {
    const message = `
      ✅ Réactiver l'utilisateur "${user.firstName || user.prenom} ${user.lastName || user.nom}" ?
      
      Cette action aura les conséquences suivantes :
      • L'utilisateur pourra à nouveau se connecter
      • Ses accès seront restaurés
      • Il recevra ses permissions selon son rôle
    `;
    
    handleUserAction('activate', user, message);
  };

  // ✅ NOUVEAU: Exécuter l'action confirmée
  const executeConfirmedAction = async () => {
    if (!confirmAction) return;
    
    const { action, user } = confirmAction;
    
    try {
      setActionLoading(prev => ({ ...prev, [user.id]: true }));
      setShowConfirmModal(false);
      
      let response;
      let successMessage;
      
      if (action === 'deactivate') {
        response = await userService.deactivateUser(user.id);
        successMessage = `${user.firstName || user.prenom} ${user.lastName || user.nom} a été désactivé avec succès`;
      } else if (action === 'activate') {
        response = await userService.activateUser(user.id);
        successMessage = `${user.firstName || user.prenom} ${user.lastName || user.nom} a été réactivé avec succès`;
      }
      
      if (response.success) {
        setSuccess(successMessage);
        await loadUsers(); // Recharger la liste
      } else {
        throw new Error(response.message);
      }

    } catch (error) {
      console.error(`❌ Erreur ${action}:`, error);
      setError(error.message || `Erreur lors de l'${action === 'activate' ? 'activation' : 'désactivation'}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [user.id]: false }));
      setConfirmAction(null);
    }
  };

  // ✅ Callback de création/modification
  const handleUserCreated = async (newUser) => {
    try {
      console.log('✅ Utilisateur créé/modifié reçu:', newUser);
      
      if (!newUser || !newUser.id) {
        console.error('❌ Données utilisateur invalides:', newUser);
        setErrors({ submit: 'Erreur: données utilisateur invalides' });
        return;
      }

      // Message de succès
      const message = selectedUser 
        ? `Utilisateur ${newUser.firstName} ${newUser.lastName} modifié avec succès !`
        : `Utilisateur ${newUser.firstName} ${newUser.lastName} créé avec succès !`;
      
      setSuccess(message);
      
      // Fermer le modal
      setShowCreateModal(false);
      setSelectedUser(null);
      setErrors({});
      
      // Recharger les données
      await loadUsers();
      
      // Effacer le message de succès après 5 secondes
      setTimeout(() => {
        setSuccess(null);
      }, 5000);

    } catch (error) {
      console.error('❌ Erreur handleUserCreated:', error);
      setErrors({ submit: 'Erreur lors de la mise à jour de la liste' });
    }
  };

  // ✅ Utilitaires
  const handleRefreshData = async () => {
    console.log('🔄 Actualisation des données...');
    await loadUsers();
  };

  const handleExportUsers = async () => {
    console.log('📤 Export des utilisateurs...');
    try {
      const exportData = filteredUsers.map(user => ({
        'Prénom': user.firstName || user.prenom,
        'Nom': user.lastName || user.nom,
        'Email': user.email,
        'Téléphone': user.phone,
        'Nom utilisateur': user.username,
        'Rôle': user.role,
        'Boutique': user.boutiqueName || 'Non assigné',
        'Statut': isUserActive(user) ? 'Actif' : 'Inactif',
        'Créé le': formatDate(user.createdAt || user.created_at)
      }));

      const csv = convertToCSV(exportData);
      downloadCSV(csv, `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`);
      
      setSuccess('Export réussi !');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('❌ Erreur export:', error);
      setError('Erreur lors de l\'export');
    }
  };

  const clearErrors = () => {
    setErrors({});
    setError(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'SUPER_ADMIN': { bg: 'danger', text: 'Super Admin', icon: Shield },
      'ADMIN': { bg: 'warning', text: 'Admin', icon: Key },
      'MANAGER': { bg: 'primary', text: 'Manager', icon: UserCheck },
      'EMPLOYEE': { bg: 'success', text: 'Employé', icon: Users }
    };
    
    const config = roleConfig[role] || roleConfig['EMPLOYEE'];
    const IconComponent = config.icon;
    
    return (
      <Badge bg={config.bg} className="d-flex align-items-center gap-1">
        <IconComponent size={12} />
        {config.text}
      </Badge>
    );
  };

  const getStatusBadge = (user) => {
    const isActive = isUserActive(user);
    return (
      <Badge bg={isActive ? 'success' : 'danger'} className="d-flex align-items-center gap-1">
        {isActive ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
        {isActive ? 'Actif' : 'Inactif'}
      </Badge>
    );
  };

  // ✅ Utilitaires CSV
  const convertToCSV = (data) => {
    if (data.length === 0) return '';
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
      Object.values(row).map(value => 
        `"${String(value).replace(/"/g, '""')}"`
      ).join(',')
    );
    return [headers, ...rows].join('\n');
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ✅ Loading initial
  if (loading && users.length === 0) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Chargement des utilisateurs...</p>
        </div>
      </Container>
    );
  }

  // ✅ NOUVEAU : Fonction pour obtenir le badge productAccessType
  const getProductAccessBadge = (productAccessType) => {
    const accessConfig = {
      'ALL': { 
        bg: 'success', 
        text: '📦🔧 Accès complet', 
        icon: Package,
        description: 'Peut gérer produits stockables et services'
      },
      'STOCKABLE': { 
        bg: 'primary', 
        text: '📦 Stockables', 
        icon: Package,
        description: 'Uniquement produits stockables'
      },
      'SERVICE': { 
        bg: 'info', 
        text: '🔧 Services', 
        icon: Package,
        description: 'Uniquement services'
      }
    };
    
    const config = accessConfig[productAccessType] || accessConfig['ALL'];
    const IconComponent = config.icon;
    
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{config.description}</Tooltip>}
      >
        <Badge bg={config.bg} className="d-flex align-items-center gap-1">
          <IconComponent size={12} />
          {config.text}
        </Badge>
      </OverlayTrigger>
    );
  };

  return (
    <Container fluid className="py-4">
      {/* En-tête */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-1">
                <Users className="me-2" size={28} />
                Gestion des Utilisateurs
              </h1>
              <p className="text-muted mb-0">
                {currentUser?.role === 'SUPER_ADMIN' 
                  ? 'Gérez tous les utilisateurs du système'
                  : `Gérez l'équipe de ${currentBoutique?.name || 'votre boutique'}`
                }
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-primary" 
                onClick={handleRefreshData}
                disabled={loading}
              >
                <RefreshCw size={16} className={`me-1 ${loading ? 'spin' : ''}`} />
                Actualiser
              </Button>
              <Button 
                variant="outline-info" 
                onClick={handleExportUsers}
                disabled={loading || filteredUsers.length === 0}
              >
                <Download size={16} className="me-1" />
                Exporter
              </Button>
              <Button 
                variant="primary" 
                onClick={handleCreateUser}
              >
                <Plus size={16} className="me-1" />
                Nouvel Utilisateur
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alertes */}
      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* ✅ NOUVEAU: Statistiques améliorées */}
      <Row className="mb-4">
        <Col lg={2} md={4} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-primary rounded p-3 me-3">
                  <Users size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="mb-0">{userStats.totalUsers}</h4>
                  <small className="text-muted">Total</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={2} md={4} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-success rounded p-3 me-3">
                  <CheckCircle size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="mb-0">{userStats.activeUsers}</h4>
                  <small className="text-muted">Actifs</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={2} md={4} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-danger rounded p-3 me-3">
                  <AlertCircle size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="mb-0">{userStats.inactiveUsers}</h4>
                  <small className="text-muted">Inactifs</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={2} md={4} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-primary rounded p-3 me-3">
                  <UserCheck size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="mb-0">{userStats.managers}</h4>
                  <small className="text-muted">Managers</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={2} md={4} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-info rounded p-3 me-3">
                  <Users size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="mb-0">{userStats.employees}</h4>
                  <small className="text-muted">Employés</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={2} md={4} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-warning rounded p-3 me-3">
                  <Shield size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="mb-0">{userStats.superAdmins}</h4>
                  <small className="text-muted">Super Admins</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ✅ NOUVEAU: Filtres avancés */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3">
                {/* Recherche */}
                <Col lg={3} md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Rechercher un utilisateur..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                
                {/* Filtre rôle */}
                <Col lg={2} md={3}>
                  <Form.Select
                    value={filters.role}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                  >
                    <option value="all">Tous les rôles</option>
                    {currentUser?.role === 'SUPER_ADMIN' && (
                      <option value="SUPER_ADMIN">Super Admin</option>
                    )}
                    <option value="MANAGER">Manager</option>
                    <option value="EMPLOYEE">Employé</option>
                  </Form.Select>
                </Col>
                
                {/* Filtre statut */}
                <Col lg={2} md={3}>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="ACTIVE">✅ Actifs</option>
                    <option value="INACTIVE">❌ Inactifs</option>
                  </Form.Select>
                </Col>

                {/* ✅ NOUVEAU: Filtre par date */}
                <Col lg={2} md={3}>
                  <Form.Select
                    value={filters.dateRange}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  >
                    <option value="all">Toutes les dates</option>
                    <option value="today">Aujourd'hui</option>
                    <option value="week">Cette semaine</option>
                    <option value="month">Ce mois</option>
                    <option value="quarter">Ce trimestre</option>
                  </Form.Select>
                </Col>

                {/* Filtre boutique (Super Admin seulement) */}
                {currentUser?.role === 'SUPER_ADMIN' && (
                  <Col lg={3} md={6}>
                    <Form.Select
                      value={filters.boutiqueId}
                      onChange={(e) => handleFilterChange('boutiqueId', e.target.value)}
                    >
                      <option value="all">Toutes les boutiques</option>
                      {boutiques.map(boutique => (
                        <option key={boutique.id} value={boutique.id}>
                          🏪 {boutique.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                )}
              </Row>
              
              {/* ✅ NOUVEAU: Options de tri */}
              <Row className="mt-3 pt-3 border-top">
                <Col lg={3} md={6}>
                  <Form.Label className="small text-muted mb-1">Trier par :</Form.Label>
                  <Form.Select 
                    size="sm"
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  >
                    <option value="lastName">Nom</option>
                    <option value="firstName">Prénom</option>
                    <option value="email">Email</option>
                    <option value="role">Rôle</option>
                    <option value="createdAt">Date de création</option>
                  </Form.Select>
                </Col>
                <Col lg={2} md={3}>
                  <Form.Label className="small text-muted mb-1">Ordre :</Form.Label>
                  <Form.Select 
                    size="sm"
                    value={filters.sortDirection}
                    onChange={(e) => handleFilterChange('sortDirection', e.target.value)}
                  >
                    <option value="ASC">Croissant</option>
                    <option value="DESC">Décroissant</option>
                  </Form.Select>
                </Col>
                <Col lg={7} md={3} className="d-flex align-items-end">
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => {
                      setFilters({
                        role: 'all',
                        status: 'all',
                        boutiqueId: currentUser?.role === 'SUPER_ADMIN' ? 'all' : (currentBoutique?.id || ''),
                        search: '',
                        dateRange: 'all',
                        sortBy: 'lastName',
                        sortDirection: 'ASC'
                      });
                      setSearchQuery('');
                    }}
                  >
                    Réinitialiser filtres
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Liste des utilisateurs */}
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Utilisateur</th>
                  <th>Contact</th>
                  <th>Rôle</th>
                  <th>Boutique</th>
                  {/* ✅ NOUVELLE COLONNE */}
                  <th>Accès Produits</th>
                  <th>Statut</th>
                  <th>Créé le</th>
                  <th width="120">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div 
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold' }}
                        >
                          {`${user.firstName?.[0] || user.prenom?.[0] || ''}${user.lastName?.[0] || user.nom?.[0] || ''}`.toUpperCase()}
                        </div>
                        <div>
                          <div className="fw-medium">
                            {`${user.firstName || user.prenom || ''} ${user.lastName || user.nom || ''}`.trim()}
                          </div>
                          <small className="text-muted">@{user.username}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        {user.email && (
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <Mail size={12} className="text-muted" />
                            <small>{user.email}</small>
                          </div>
                        )}
                        {user.phone && (
                          <div className="d-flex align-items-center gap-1">
                            <Phone size={12} className="text-muted" />
                            <small>{user.phone}</small>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {getRoleBadge(user.role)}
                    </td>
                    <td>
                      <small>{user.boutiqueName || '-'}</small>
                    </td>
                    {/* ✅ NOUVELLE CELLULE pour productAccessType */}
                    <td>
                      {(user.role === 'MANAGER' || user.role === 'EMPLOYEE') ? (
                        getProductAccessBadge(user.productAccessType || 'ALL')
                      ) : (
                        <small className="text-muted">N/A</small>
                      )}
                    </td>
                    <td>
                      <Badge bg={isUserActive(user) ? "success" : "secondary"}>
                        {isUserActive(user) ? "✅ Actif" : "❌ Inactif"}
                      </Badge>
                    </td>
                    <td>
                      <small className="text-muted">
                        {(user.createdAt || user.created_at)
                          ? new Date(user.createdAt || user.created_at).toLocaleDateString('fr-FR')
                          : '-'
                        }
                      </small>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          size="sm" 
                          variant="outline-primary"
                          onClick={() => handleViewUser(user)}
                        >
                          <Eye size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-secondary"
                          onClick={() => handleEditUser(user)}
                        >
                          <Edit2 size={14} />
                        </Button>
                        {/* ... autres boutons existants */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
        </Col>
      </Row>

      {/* Modal de création/édition */}
      <CreateUser
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setSelectedUser(null);
          clearErrors();
        }}
        onUserCreated={handleUserCreated}
        selectedBoutiqueId={
          currentUser?.role === 'SUPER_ADMIN' 
            ? null 
            : currentBoutique?.id
        }
        editUser={selectedUser}
      />

      {/* ✅ NOUVEAU: Modal de confirmation */}
      <Modal 
        show={showConfirmModal} 
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmAction?.action === 'activate' ? '✅ Confirmer activation' : '⚠️ Confirmer désactivation'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction && (
            <div style={{ whiteSpace: 'pre-line' }}>
              {confirmAction.message}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Annuler
          </Button>
          <Button 
            variant={confirmAction?.action === 'activate' ? 'success' : 'danger'} 
            onClick={executeConfirmedAction}
          >
            {confirmAction?.action === 'activate' ? 'Activer' : 'Désactiver'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal détails utilisateur */}
      <Modal 
        show={showDetailsModal} 
        onHide={() => setShowDetailsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Détails - {selectedUser?.prenom || selectedUser?.firstName} {selectedUser?.nom || selectedUser?.lastName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Row>
              <Col md={4} className="text-center mb-4">
                <div 
                  className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{ width: '80px', height: '80px', fontSize: '32px', fontWeight: 'bold' }}
                >
                  {(selectedUser.prenom || selectedUser.firstName || 'U').charAt(0).toUpperCase()}
                </div>
                <h5>{selectedUser.firstName || selectedUser.prenom} {selectedUser.nom || selectedUser.lastName}</h5>
                <div className="mb-2">{getRoleBadge(selectedUser.role)}</div>
                {getStatusBadge(selectedUser)}
              </Col>
              <Col md={8}>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <h6 className="mb-3">Informations de contact</h6>
                    <div className="mb-2">
                      <Mail size={16} className="text-muted me-2" />
                      <span>{selectedUser.email}</span>
                    </div>
                    {selectedUser.phone && (
                      <div className="mb-2">
                        <Phone size={16} className="text-muted me-2" />
                        <span>{selectedUser.phone}</span>
                      </div>
                    )}
                    <div className="mb-3">
                      <Users size={16} className="text-muted me-2" />
                      <span>@{selectedUser.username}</span>
                    </div>
                    
                    {selectedUser.boutiqueName && (
                      <>
                        <h6 className="mb-3">Boutique assignée</h6>
                        <div className="d-flex align-items-center">
                          <Building size={16} className="text-muted me-2" />
                          <div>
                            <div className="fw-bold">{selectedUser.boutiqueName}</div>
                            <small className="text-muted">ID: {selectedUser.boutiqueId}</small>
                          </div>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Fermer
          </Button>
          {selectedUser && (
            <Button variant="warning" onClick={() => {
              setShowDetailsModal(false);
              handleEditUser(selectedUser);
            }}>
              <Edit size={16} className="me-1" />
              Modifier
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

// CSS pour les animations
const style = document.createElement('style');
style.textContent = `
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;
document.head.appendChild(style);

export default UsersList;